import { useEffect, useState } from "react";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import PageHeader from "@/components/PageHeader";
import FieldGuide from "@/components/FieldGuide";
import { fmtNum, fmtUsd } from "@/lib/format";

const SECTORS = [
  "Buildings",
  "Infrastructure",
  "Energy",
  "Oil & Gas",
  "Mining",
  "Manufacturing",
  "Defence",
  "Water & Wastewater",
  "Telecommunications",
];

const SUB_SECTORS_BY_SECTOR = {
  Buildings: ["Residential", "Commercial", "Healthcare", "Education", "Industrial", "Data Centres"],
  Infrastructure: ["Roads", "Rail", "Airports", "Ports", "Utilities", "Bridges", "Tunnels"],
  Energy: ["Solar", "Wind", "Hydrogen", "Battery Storage", "Nuclear"],
  "Oil & Gas": ["Upstream", "Midstream", "Downstream"],
  Mining: ["Iron Ore", "Copper", "Lithium", "Coal"],
  Manufacturing: ["Heavy", "Light", "Electronics"],
  Defence: ["Land", "Naval", "Aerospace"],
  "Water & Wastewater": ["Treatment", "Distribution", "Desalination"],
  Telecommunications: ["Fibre", "Tower", "Data Centre"],
};

export default function Projects() {
  const { projects, refresh, selectProject, activeId } = useProjects();
  const [countries, setCountries] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [form, setForm] = useState({
    name: "",
    sector: "Buildings",
    sub_sector: "Commercial",
    country_code: "GB",
    floor_area_m2: 10000,
    cost_usd: 50000000,
    target_intensity_kg_per_m2: 500,
    carbon_price_id: "icp-mid",
    description: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data.items));
    api.get("/carbon-schemes").then((r) => setSchemes(r.data.items));
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/projects", form);
      toast.success("Project created");
      await refresh();
      selectProject(data.id);
      setForm({ ...form, name: "", description: "" });
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this project and all data?")) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      await refresh();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader overline="Workspace" title="Projects" subtitle="Manage your carbon-tracked portfolio."
        guideId="projects"
      />

      <section className="px-8 pt-8 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <form
          onSubmit={handleCreate}
          className="xl:col-span-1 border border theme-border theme-surface p-6 space-y-3"
          data-testid="project-create-form"
        >
          <div className="overline">New Project</div>
          <h2 className="font-serif-display text-2xl">Create</h2>

          <div>
            <label className="overline flex items-center gap-1 mb-1.5">Name <FieldGuide field="project.name" /></label>
            <input
              data-testid="project-name-input"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full theme-input border border theme-border px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-gold"
              placeholder="Project Riverside"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline flex items-center gap-1 mb-1.5">Sector <FieldGuide field="project.sector" /></label>
              <select
                value={form.sector}
                onChange={(e) =>
                  setForm({ ...form, sector: e.target.value, sub_sector: SUB_SECTORS_BY_SECTOR[e.target.value][0] })
                }
                className="w-full theme-input border border theme-border px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-gold"
              >
                {SECTORS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="overline flex items-center gap-1 mb-1.5">Sub-sector <FieldGuide field="project.sub_sector" /></label>
              <select
                value={form.sub_sector}
                onChange={(e) => setForm({ ...form, sub_sector: e.target.value })}
                className="w-full theme-input border border theme-border px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-gold"
              >
                {(SUB_SECTORS_BY_SECTOR[form.sector] || []).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="overline flex items-center gap-1 mb-1.5">Country <FieldGuide field="project.country_code" /></label>
            <select
              data-testid="project-country-select"
              value={form.country_code}
              onChange={(e) => setForm({ ...form, country_code: e.target.value })}
              className="w-full theme-input border border theme-border px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-gold"
            >
              {countries.map((c) => (
                <option key={c.code} value={c.code}>{c.name} · {c.framework}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline flex items-center gap-1 mb-1.5">Floor area (m²) <FieldGuide field="project.floor_area_m2" /></label>
              <input
                type="number"
                value={form.floor_area_m2}
                onChange={(e) => setForm({ ...form, floor_area_m2: Number(e.target.value) })}
                className="w-full theme-input border border theme-border px-3 py-2 text-sm rounded-sm font-mono-data focus:outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="overline flex items-center gap-1 mb-1.5">Cost (USD) <FieldGuide field="project.cost_usd" /></label>
              <input
                type="number"
                value={form.cost_usd}
                onChange={(e) => setForm({ ...form, cost_usd: Number(e.target.value) })}
                className="w-full theme-input border border theme-border px-3 py-2 text-sm rounded-sm font-mono-data focus:outline-none focus:border-gold"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline flex items-center gap-1 mb-1.5">Target kg/m² <FieldGuide field="project.target_intensity_kg_per_m2" /></label>
              <input
                type="number"
                value={form.target_intensity_kg_per_m2}
                onChange={(e) => setForm({ ...form, target_intensity_kg_per_m2: Number(e.target.value) })}
                className="w-full theme-input border border theme-border px-3 py-2 text-sm rounded-sm font-mono-data focus:outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="overline flex items-center gap-1 mb-1.5">Carbon price <FieldGuide field="project.carbon_price_id" /></label>
              <select
                value={form.carbon_price_id}
                onChange={(e) => setForm({ ...form, carbon_price_id: e.target.value })}
                className="w-full theme-input border border theme-border px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-gold"
              >
                {schemes.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} (${s.price_usd_per_t}/t)</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="overline block mb-1.5">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="w-full theme-input border border theme-border px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-gold"
              placeholder="Optional notes"
            />
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="project-create-btn"
            className="w-full bg-gold text-black font-medium py-2.5 rounded-sm hover:bg-[#e0bc40] disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Plus size={16} weight="bold" /> {busy ? "Creating…" : "Create project"}
          </button>
        </form>

        <div className="xl:col-span-2 border border theme-border theme-surface overflow-hidden">
          <div className="px-6 py-4 border-b theme-border flex items-baseline justify-between">
            <div>
              <div className="overline">Portfolio</div>
              <h2 className="font-serif-display text-2xl">{projects.length} project{projects.length === 1 ? "" : "s"}</h2>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-black/[0.02] dark:bg-white/[0.02] text-[10px] uppercase tracking-[0.2em] theme-ink-dim">
              <tr>
                <th className="text-left px-6 py-3">Name</th>
                <th className="text-left px-4 py-3">Sector</th>
                <th className="text-left px-4 py-3">Country</th>
                <th className="text-right px-4 py-3">Area (m²)</th>
                <th className="text-right px-4 py-3">Cost</th>
                <th className="text-right px-4 py-3">Target</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`project-row-${p.id}`}
                  onClick={() => selectProject(p.id)}
                  className={`border-t border-white/5 cursor-pointer hover:bg-black/[0.02] dark:bg-white/[0.02] ${
                    activeId === p.id ? "bg-white/[0.03]" : ""
                  }`}
                >
                  <td className="px-6 py-3">
                    <div className="theme-ink">{p.name}</div>
                    <div className="text-[11px] theme-ink-dim mt-0.5 line-clamp-1">{p.description}</div>
                  </td>
                  <td className="px-4 theme-ink-muted">{p.sector}</td>
                  <td className="px-4 font-mono-data theme-ink-muted">{p.country_code}</td>
                  <td className="px-4 py-3 text-right font-mono-data">{fmtNum(p.floor_area_m2)}</td>
                  <td className="px-4 py-3 text-right font-mono-data theme-ink-muted">{fmtUsd(p.cost_usd)}</td>
                  <td className="px-4 py-3 text-right font-mono-data text-eco">{fmtNum(p.target_intensity_kg_per_m2, 0)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      data-testid={`project-delete-${p.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(p.id);
                      }}
                      className="theme-ink-dim hover:text-risk transition-colors"
                      aria-label="Delete"
                    >
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {projects.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center theme-ink-dim text-sm">
                    No projects yet — create one to begin.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
